import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { sessionState } from '../session/atom';

import {
  masterStateAdvisorListLoaded,
  masterStateAdvisorListSelector,
  masterStateSelectedAdvisorGroupSelector,
  masterStateSelectedAdvisorSelector,
  masterStateSelectedPartnerSelector,
} from './selector';
import { MasterStateProps } from './types';

export const useMasterStateActions = () => {
  const { userProfile } = useRecoilValue(sessionState);
  const advisorList = useRecoilValue(masterStateAdvisorListSelector);
  const setAdvisorList = useSetRecoilState(masterStateAdvisorListSelector);
  const setAdvisorListLoaded = useSetRecoilState(masterStateAdvisorListLoaded);
  const setSelectedAdvisor = useSetRecoilState(masterStateSelectedAdvisorSelector);
  const setSelectedAdvisorGroup = useSetRecoilState(masterStateSelectedAdvisorGroupSelector);
  const setSelectedPartner = useSetRecoilState(masterStateSelectedPartnerSelector);

  const setAdvisorGroupFromAdvisorId = useCallback(
    (advisorId: number | undefined) => {
      if (!advisorId) {
        setSelectedAdvisorGroup(undefined);
        return;
      }

      const selectedAdvisorGroup = advisorList.find((advisor) => advisor.id === advisorId);

      if (selectedAdvisorGroup) {
        setSelectedAdvisorGroup({
          partnerName: selectedAdvisorGroup.partnerName,
          branchId: selectedAdvisorGroup.branchId,
          partnerId: selectedAdvisorGroup.partnerId,
        });
      } else {
        setSelectedAdvisorGroup(undefined);
      }
    },
    [advisorList, setSelectedAdvisorGroup]
  );

  const setMasterState = (newValue: MasterStateProps) => {
    setAdvisorListLoaded(newValue.loaded);
    setAdvisorList(newValue.advisorList);
    setSelectedAdvisor(newValue.selectedAdvisor);
    setSelectedPartner(newValue.selectedPartner);

    setAdvisorGroupFromAdvisorId(newValue.selectedAdvisor);
  };

  const setNewSelectedAdvisor = (advisorId: MasterStateProps['selectedAdvisor']) => {
    setSelectedAdvisor(advisorId);
    setAdvisorGroupFromAdvisorId(advisorId);
  };

  const setNewSelectedPartner = (partnerId: MasterStateProps['selectedPartner']) => {
    setSelectedPartner(partnerId);
  };

  const clearSelectedAdvisor = useCallback(() => {
    setSelectedAdvisor(undefined);
    setAdvisorGroupFromAdvisorId(undefined);
    setSelectedPartner(userProfile?.idParceiro);
  }, [
    setAdvisorGroupFromAdvisorId,
    setSelectedAdvisor,
    setSelectedPartner,
    userProfile?.idParceiro,
  ]);

  return {
    setMasterState,
    setNewSelectedAdvisor,
    setNewSelectedPartner,
    clearSelectedAdvisor,
  };
};
